// frontend/src/layouts/AdminLayout.jsx
// Updated to include theme switcher in sidebar
import React, { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, logout } from '../auth/auth';
import { ThemeSwitcher } from '../components/ui/ThemeSwitcher';

const AdminLayout = () => {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [sessionWarning, setSessionWarning] = useState(false);

  const handleLogout = useCallback(() => {
    logout();
    setUser(null);
    navigate('/login');
  }, [setUser, navigate]);

  // Redirect non-admin users
  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else if (!user.isAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Session timeout monitoring - 30 minute inactivity timeout
  useEffect(() => {
    const activityHandler = () => {
      setLastActivity(Date.now());
    };

    // Listen for user activity
    window.addEventListener('click', activityHandler);
    window.addEventListener('keypress', activityHandler);
    window.addEventListener('scroll', activityHandler);
    window.addEventListener('mousemove', activityHandler);

    // Check activity every minute
    const intervalId = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      if (timeSinceLastActivity > 30 * 60 * 1000 && !sessionWarning) {
        setSessionWarning(true);
      }
    }, 60000);

    return () => {
      window.removeEventListener('click', activityHandler);
      window.removeEventListener('keypress', activityHandler);
      window.removeEventListener('scroll', activityHandler);
      window.removeEventListener('mousemove', activityHandler);
      clearInterval(intervalId);
    };
  }, [lastActivity, sessionWarning]);

  if (!user || !user.isAdmin) {
    return <div className="p-6 text-center text-status-error-text">Unauthorized access. Please log in as an admin.</div>;
  }

  const isActiveRoute = (route) => location.pathname === route || location.pathname.startsWith(`${route}/`);
  const navLinkClass = (route) =>
    `flex items-center px-4 py-2 rounded-md ${
      isActiveRoute(route) ? 'bg-button-primary-bg text-button-primary-text' : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary'
    }`;

  return (
    <div className="flex h-screen bg-background-primary">
      {sessionWarning && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-background-secondary p-6 rounded-lg shadow-xl max-w-md w-full border border-border-primary">
            <h3 className="text-lg font-medium text-text-primary mb-3">Session Timeout Warning</h3>
            <p className="text-text-secondary mb-4">
              Your session will expire in less than 5 minutes due to inactivity. Do you want to stay logged in?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleLogout}
                className="bg-button-secondary-bg text-button-secondary-text py-2 px-4 rounded hover:bg-button-secondary-hover"
              >
                Log out
              </button>
              <button
                onClick={() => {
                  setLastActivity(Date.now());
                  setSessionWarning(false);
                }}
                className="bg-button-primary-bg text-button-primary-text py-2 px-4 rounded hover:bg-button-primary-hover"
              >
                Stay logged in
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-30 w-64 bg-background-secondary transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:static md:inset-auto`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-border-primary">
            <Link to="/admin" className="text-xl font-bold text-text-primary">
              Admin Panel
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden text-text-secondary hover:text-text-primary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="px-2 pb-4 space-y-1">
            <Link to="/admin" className={navLinkClass('/admin')} onClick={() => setSidebarOpen(false)}>Dashboard</Link>
            <Link to="/admin/leads" className={navLinkClass('/admin/leads')} onClick={() => setSidebarOpen(false)}>Leads</Link>
            <Link to="/admin/database" className={navLinkClass('/admin/database')} onClick={() => setSidebarOpen(false)}>Database Leads</Link>
            <Link to="/admin/unbounce" className={navLinkClass('/admin/unbounce')} onClick={() => setSidebarOpen(false)}>Unbounce Leads</Link>
            <Link to="/admin/agents" className={navLinkClass('/admin/agents')} onClick={() => setSidebarOpen(false)}>Agents</Link>
            <Link to="/admin/transactions" className={navLinkClass('/admin/transactions')} onClick={() => setSidebarOpen(false)}>Transactions</Link>
            <Link to="/admin/settings" className={navLinkClass('/admin/settings')} onClick={() => setSidebarOpen(false)}>Settings</Link>
            <Link to="/admin/emails" className={navLinkClass('/admin/emails')} onClick={() => setSidebarOpen(false)}>Email Management</Link>
            <Link to="/admin/email-tracking" className={navLinkClass('/admin/email-tracking')} onClick={() => setSidebarOpen(false)}>Email Tracking</Link>
            <Link to="/admin/api-monitor" className={navLinkClass('/admin/api-monitor')} onClick={() => setSidebarOpen(false)}>API Monitor</Link>
            <Link to="/dashboard" className="flex items-center px-4 py-2 mt-4 text-text-secondary hover:bg-background-tertiary hover:text-text-primary rounded-md" onClick={() => setSidebarOpen(false)}>
              Back to Agent View
            </Link>
            <button
              onClick={handleLogout}
              className="flex w-full items-center px-4 py-2 text-text-secondary hover:bg-background-tertiary hover:text-text-primary rounded-md"
            >
              Sign Out
            </button>
            
            {/* Theme Switcher */}
            <div className="mt-4 px-4">
              <ThemeSwitcher />
            </div>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Navigation */}
        <header className="bg-background-secondary border-b border-border-primary p-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden text-text-secondary hover:text-text-primary"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center">
            <span className="text-text-primary font-medium">Welcome, {user?.name || 'Admin'}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;